import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Sparkles, CalendarPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_TOKEN_KEY = "scc_session_token";

const fmtErr = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
};

const emptyChild = () => ({ given_name: "", last_name: "", birthdate: "", gender: "", medical_info: "" });

export default function AcademyAdhocSignup() {
  const navigate = useNavigate();
  const [parent, setParent] = useState({ parent_given_name: "", parent_last_name: "", parent_email: "", parent_phone: "", parent_password: "" });
  const [children, setChildren] = useState([emptyChild()]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setP = (k, v) => setParent((p) => ({ ...p, [k]: v }));
  const setC = (i, k, v) => setChildren((cs) => cs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const addChild = () => setChildren((cs) => [...cs, emptyChild()]);
  const removeChild = (i) => setChildren((cs) => cs.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!parent.parent_given_name || !parent.parent_last_name || !parent.parent_email || !parent.parent_password) {
      toast.error("Please fill in your name, email and password."); return;
    }
    if (parent.parent_password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    const validKids = children.filter((c) => c.given_name.trim() && c.last_name.trim());
    if (!validKids.length) { toast.error("Please add at least one child (name)."); return; }
    if (!consent) { toast.error("Please accept the consent acknowledgement to continue."); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/academy/adhoc-register`, { ...parent, children: validKids });
      // Auto-login via the existing login endpoint (no new auth)
      const login = await axios.post(`${API}/auth/login`, { email: parent.parent_email, password: parent.parent_password }, { withCredentials: true });
      const token = login.data?.session_token;
      if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
      toast.success("Account created! You can now book single sessions.");
      window.location.href = "/academy/parent-dashboard";
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Sign-up failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/academy/start-registration" className="inline-flex items-center text-slate-400 hover:text-white text-sm mb-4" data-testid="adhoc-back"><ArrowLeft className="w-4 h-4 mr-1" />Back</Link>
        <div className="flex items-center gap-2 mb-1">
          <CalendarPlus className="w-6 h-6 text-orange-400" />
          <h1 className="text-2xl font-bold">Book a single session</h1>
        </div>
        <p className="text-sm text-slate-400 mb-6">Pay-as-you-go — no full-term commitment. Create your account and a child profile, then book any available session and pay per visit.</p>

        {/* Parent */}
        <Card className="bg-slate-900/60 border-slate-800 p-5 mb-4">
          <h2 className="font-semibold mb-3">Your details</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-400">First name</Label><Input data-testid="adhoc-parent-given" value={parent.parent_given_name} onChange={(e) => setP("parent_given_name", e.target.value)} className="bg-slate-800 border-slate-700" /></div>
            <div><Label className="text-xs text-slate-400">Last name</Label><Input data-testid="adhoc-parent-last" value={parent.parent_last_name} onChange={(e) => setP("parent_last_name", e.target.value)} className="bg-slate-800 border-slate-700" /></div>
            <div><Label className="text-xs text-slate-400">Email</Label><Input data-testid="adhoc-parent-email" type="email" value={parent.parent_email} onChange={(e) => setP("parent_email", e.target.value)} className="bg-slate-800 border-slate-700" /></div>
            <div><Label className="text-xs text-slate-400">Phone</Label><Input data-testid="adhoc-parent-phone" value={parent.parent_phone} onChange={(e) => setP("parent_phone", e.target.value)} className="bg-slate-800 border-slate-700" /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-slate-400">Password</Label><Input data-testid="adhoc-parent-password" type="password" value={parent.parent_password} onChange={(e) => setP("parent_password", e.target.value)} className="bg-slate-800 border-slate-700" /></div>
          </div>
        </Card>

        {/* Children */}
        <Card className="bg-slate-900/60 border-slate-800 p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Child details</h2>
            <Button size="sm" variant="outline" className="border-slate-600 text-xs" onClick={addChild} data-testid="adhoc-add-child"><Plus className="w-3 h-3 mr-1" />Add child</Button>
          </div>
          {children.map((c, i) => (
            <div key={i} className="border border-slate-800 rounded-lg p-3 mb-3" data-testid={`adhoc-child-${i}`}>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-400">First name</Label><Input value={c.given_name} onChange={(e) => setC(i, "given_name", e.target.value)} className="bg-slate-800 border-slate-700" data-testid={`adhoc-child-given-${i}`} /></div>
                <div><Label className="text-xs text-slate-400">Last name</Label><Input value={c.last_name} onChange={(e) => setC(i, "last_name", e.target.value)} className="bg-slate-800 border-slate-700" data-testid={`adhoc-child-last-${i}`} /></div>
                <div><Label className="text-xs text-slate-400">Date of birth</Label><Input type="date" value={c.birthdate} onChange={(e) => setC(i, "birthdate", e.target.value)} className="bg-slate-800 border-slate-700" data-testid={`adhoc-child-dob-${i}`} /></div>
                <div>
                  <Label className="text-xs text-slate-400">Gender</Label>
                  <Select value={c.gender} onValueChange={(v) => setC(i, "gender", v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700" data-testid={`adhoc-child-gender-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-slate-100"><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="text-xs text-slate-400">Medical notes (optional)</Label><Input value={c.medical_info} onChange={(e) => setC(i, "medical_info", e.target.value)} placeholder="Allergies, conditions… (optional)" className="bg-slate-800 border-slate-700" data-testid={`adhoc-child-medical-${i}`} /></div>
              </div>
              {children.length > 1 && <Button size="sm" variant="ghost" className="text-red-400 mt-2 text-xs" onClick={() => removeChild(i)} data-testid={`adhoc-remove-child-${i}`}><Trash2 className="w-3 h-3 mr-1" />Remove</Button>}
            </div>
          ))}
        </Card>

        {/* Become a member CTA */}
        <Card className="bg-gradient-to-r from-amber-500/10 to-amber-700/10 border-amber-500/30 p-4 mb-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-200">Play often? Membership pays off.</p>
              <p className="text-sm text-slate-300 mt-1">Ad-hoc sessions include a walk-in premium. A Digital Membership or term registration unlocks member rates and perks. You can always upgrade later from your dashboard.</p>
              <Link to="/academy/start-registration" className="text-amber-300 text-sm underline mt-1 inline-block" data-testid="adhoc-become-member">See membership &amp; term options →</Link>
            </div>
          </div>
        </Card>

        <label className="flex items-start gap-2 text-sm text-slate-300 mb-4">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="adhoc-consent" />
          <span>I confirm the details are accurate and I accept the academy's participation terms and liability acknowledgement for drop-in sessions.</span>
        </label>

        <Button onClick={submit} disabled={submitting} className="w-full bg-orange-600 hover:bg-orange-500" data-testid="adhoc-submit">
          {submitting ? "Creating account…" : "Create account & continue"}
        </Button>
        <p className="text-center text-xs text-slate-500 mt-3">Already have an account? <Link to="/login" className="underline">Log in</Link> and use the Ad-hoc button on your dashboard.</p>
      </div>
    </div>
  );
}
